import _toConsumableArray from "/opt/build/repo/web/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/opt/build/repo/web/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { __rest } from "tslib";
import * as React from 'react';
import { Arrays } from '../..';
import { useGeneratedId, useTemporaryState, useTimeout } from '../../hooks';
import { ClassNames } from '../../utils/classNames';
import { Dates } from '../../utils/dates';
import { Objects } from '../../utils/objects';
import { IconUtils } from '../icon';
import { IconButton } from '../iconButton';
import { useToasts } from './toast.context';
/** Render a single toast notification with a title and some given information */

export var ToastNotification = React.forwardRef(function (_a, ref) {
  var onDismiss = _a.onDismiss,
      toast = __rest(_a, ["onDismiss"]);

  var autoDismissTime = toast.autoDismissTime,
      children = toast.children,
      content = toast.content,
      htmlProps = toast.htmlProps,
      timestamp = toast.timestamp,
      title = toast.title,
      type = toast.type,
      allowManualDismiss = toast.allowManualDismiss;

  var _useTemporaryState = useTemporaryState(false, 500, onDismiss),
      _useTemporaryState2 = _slicedToArray(_useTemporaryState, 2),
      dismissing = _useTemporaryState2[0],
      setDismissing = _useTemporaryState2[1];

  var beginDismiss = React.useCallback(function () {
    return setDismissing(true);
  }, []);

  var _useTimeout = useTimeout(beginDismiss, autoDismissTime),
      setAutoDismissTimeout = _useTimeout.set,
      clearAutoDismissTimeout = _useTimeout.clear;

  React.useEffect(function () {
    void setAutoDismissTimeout();
  }, []);
  var id = useGeneratedId('arm-tst_', htmlProps === null || htmlProps === void 0 ? void 0 : htmlProps.id);
  var contentNode = React.useMemo(function () {
    return typeof content === 'function' ? content({
      dismiss: beginDismiss,
      toast: toast
    }) : content;
  }, [content, beginDismiss, toast]);

  var _useToasts = useToasts(),
      timestampFormatString = _useToasts.config.timestampFormatString;

  var timestampString = React.useMemo(function () {
    return timestampFormatString && Dates.dateToString(timestamp, timestampFormatString);
  }, [timestamp]);
  return React.createElement("div", Object.assign({}, htmlProps, {
    className: ClassNames.concat('arm-toast-notification', htmlProps === null || htmlProps === void 0 ? void 0 : htmlProps.className),
    "data-type": type,
    "data-dismissing": dismissing,
    onMouseEnter: clearAutoDismissTimeout,
    onMouseLeave: function onMouseLeave() {
      return setAutoDismissTimeout();
    },
    ref: ref
  }), React.createElement("div", {
    className: "arm-toast-notification-inner",
    role: "status",
    "aria-labelledby": "".concat(id, "_title")
  }, React.createElement("div", {
    className: "arm-toast-notification-top"
  }, React.createElement("p", {
    className: "arm-toast-notification-title",
    id: "".concat(id, "_title")
  }, title), allowManualDismiss && React.createElement(IconButton, {
    type: "button",
    className: "arm-toast-notification-close-button",
    minimalStyle: true,
    icon: IconUtils.getIconDefinition('Icomoon', 'cross2'),
    onClick: beginDismiss
  })), timestampString && React.createElement("p", null, timestampString), typeof contentNode === 'string' || typeof contentNode === 'number' ? React.createElement("p", null, contentNode) : contentNode, children));
});
ToastNotification.defaultProps = {
  allowManualDismiss: true
};
/** A container which will render toasts passed in through props or toasts available from the ToastContext dispatched from useToastDispatch */

export var ToastNotificationContainer = React.forwardRef(function (_ref, ref) {
  var toasts = _ref.toasts;

  var _a, _b, _c, _d;

  var _useToasts2 = useToasts(),
      contextToasts = _useToasts2.toasts;

  var combinedToasts = React.useMemo(function () {
    return [].concat(_toConsumableArray(contextToasts || []), _toConsumableArray(toasts || []));
  }, [contextToasts, toasts]);
  var splitToasts = React.useMemo(function () {
    return Arrays.arrayToArrayDictionary(combinedToasts, function (toast) {
      return toast.position;
    });
  }, [combinedToasts]);

  if (!combinedToasts.length) {
    return null;
  }

  return React.createElement("div", {
    className: "arm-toast-notification-container",
    ref: ref
  }, React.createElement("div", {
    className: "arm-toast-notification-container-left"
  }, React.createElement("div", {
    className: "arm-toast-notification-toasts arm-toast-notification-toasts-top"
  }, !!((_a = splitToasts['top-left']) === null || _a === void 0 ? void 0 : _a.length) && splitToasts['top-left'].map(function (toast) {
    return React.createElement(ToastNotification, Object.assign({}, toast, {
      key: Objects.contentDependency(toast)
    }));
  })), React.createElement("div", {
    className: "arm-toast-notification-toasts arm-toast-notification-toasts-bottom"
  }, !!((_b = splitToasts['bottom-left']) === null || _b === void 0 ? void 0 : _b.length) && splitToasts['bottom-left'].map(function (toast) {
    return React.createElement(ToastNotification, Object.assign({}, toast, {
      key: Objects.contentDependency(toast)
    }));
  }))), React.createElement("div", {
    className: "arm-toast-notification-container-right"
  }, React.createElement("div", {
    className: "arm-toast-notification-toasts arm-toast-notification-toasts-top"
  }, !!((_c = splitToasts['top-right']) === null || _c === void 0 ? void 0 : _c.length) && splitToasts['top-right'].map(function (toast) {
    return React.createElement(ToastNotification, Object.assign({}, toast, {
      key: Objects.contentDependency(toast)
    }));
  })), React.createElement("div", {
    className: "arm-toast-notification-toasts arm-toast-notification-toasts-bottom"
  }, !!((_d = splitToasts['bottom-right']) === null || _d === void 0 ? void 0 : _d.length) && splitToasts['bottom-right'].map(function (toast) {
    return React.createElement(ToastNotification, Object.assign({}, toast, {
      key: Objects.contentDependency(toast)
    }));
  }))));
});